import { lazy, Suspense, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import Routing from '@routes/Routing';
import { doAuthentication, logout } from '@services/auth/Auth';
import Layout from '@components/layout/Layout';
import { Loading as CoreLoading } from '@agilize/components-core';
import { smartlookConsent, smartlookError, smartlookIdentify, smartLookInit } from '@utils/smartlook';
import { CompanyType, getCompanies } from '@context/CompaniesContext';
import { zendeskchatInit } from '@utils/zendeskChat';
import { ga4Init } from '@utils/ga4';
import AppProvider, { AuthProviderData } from './AppProvider';

const Toast = lazy(() => import('./utils/toastHelper').then(({ StyledToastContainer }) => ({ default: StyledToastContainer })));

const App = () => {
  const [loading, setLoading] = useState(true);
  const [authData, setAuthData] = useState<AuthProviderData>({
    userData: { name: '', email: '', tenant: [], sub: '' },
    companiesData: [{ id: '', corporateName: '', cnpj: '', isDefault: false, email: '', address: {}, hasElectronicInvoiceIssuance: true }],
  });
  const [selectedCompany, setSelectedCompany] = useState<CompanyType | undefined>();

  // @todo: duplicated function with handleChangeCompany from MainHeader.tsx
  const handleSetSelectedCompany = (companies?: CompanyType[]) => {
    if (!companies) {
      return logout();
    }

    const companyIdByCookie = Cookies.get('selectedCompanyId');
    const searchedCompany = companies?.find(company => company.id && company.id === companyIdByCookie) || companies[0];
    (window as any).personId = searchedCompany?.id;
    const isProductionOrStagingEnv = process.env.NODE_ENV === 'production' || process.env.NODE_ENV !== 'development';
    Cookies.set('selectedCompanyId', searchedCompany.id, isProductionOrStagingEnv && { domain: 'agilize.com.br' });
    setSelectedCompany(searchedCompany);
  };

  useEffect(() => {
    (async () => {
      const authData = await doAuthentication();
      if (authData?.isAuthenticated) {
        const { userData } = authData;
        const { companiesData } = await getCompanies(userData);
        setAuthData({ userData, companiesData });
        handleSetSelectedCompany(companiesData);
        setLoading(false);
        if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'hml') {
          zendeskchatInit();
          smartLookInit();
          smartlookIdentify(userData.sub, { id: userData.sub, name: userData.name, email: userData.email });
          smartlookConsent();
          ga4Init();
          window.onerror = msg => {
            smartlookError(msg);
          };
        }
      }
    })();
  }, []);

  return (
    <Layout>
      {loading ? (
        <div data-testid="loading-status" className="centered-content centered-content__full">
          <CoreLoading size={100} />
        </div>
      ) : (
        <AppProvider authData={authData} selectedCompany={selectedCompany} setSelectedCompany={setSelectedCompany}>
          <Routing />
          <Suspense fallback={<></>}>
            <Toast />
          </Suspense>
        </AppProvider>
      )}
    </Layout>
  );
};

export default App;
